import { useState } from 'react';

import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  GitBranch,
  GitCommit,
  RotateCcw,
} from 'lucide-react';

import { PromptDiff } from '@/components/PromptDiff';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  useAbandonBranch,
  useConfigBranches,
  useConfigCommits,
  useConfigDiff,
  useConfigSnapshot,
  usePromoteBranch,
  useRollbackConfig,
} from '@/hooks/useConfigRepository';
import { extractErrorMessage } from '@/lib/utils';

import type { BranchStatus } from '@/lib/api/config-repository.api';

const STATUS_VARIANT: Record<BranchStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  staged: 'secondary',
  merged: 'outline',
  abandoned: 'destructive',
};

const TEXT_FIELDS = ['prompt_template', 'text_prompt_template', 'prompt_rules', 'welcome_message'];

interface ConfirmAction {
  kind: 'promote' | 'rollback' | 'abandon';
  id: string;
  label: string;
}

export const RepositorySection = ({ configurationId }: { configurationId: string }) => {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('main');
  const [selectedCommitId, setSelectedCommitId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const {
    data: branches,
    isLoading: branchesLoading,
    error: branchesError,
  } = useConfigBranches(configurationId);
  const {
    data: commits,
    isLoading: commitsLoading,
    error: commitsError,
  } = useConfigCommits(configurationId, selectedBranch);
  const promoteMutation = usePromoteBranch(configurationId);
  const rollbackMutation = useRollbackConfig(configurationId);
  const abandonMutation = useAbandonBranch(configurationId);

  const isMutating =
    promoteMutation.isPending || rollbackMutation.isPending || abandonMutation.isPending;

  const selectedCommit = commits?.find((c) => c.id === selectedCommitId);
  const parentId = selectedCommit?.parent_commit_id ?? null;
  const isInitialCommit = selectedCommit != null && parentId === null;
  const {
    data: diffOps,
    isLoading: diffLoading,
    error: diffError,
  } = useConfigDiff(configurationId, parentId ?? undefined, selectedCommitId ?? undefined);
  const { data: beforeSnap } = useConfigSnapshot(configurationId, parentId);
  const { data: afterSnap, isLoading: snapLoading } = useConfigSnapshot(
    configurationId,
    selectedCommitId,
  );

  const mainBranch = branches?.find((b) => b.name === 'main');
  const candidateBranches = branches?.filter((b) => b.name !== 'main') ?? [];

  const executeConfirmedAction = async () => {
    if (!confirmAction) return;
    try {
      switch (confirmAction.kind) {
        case 'promote':
          await promoteMutation.mutateAsync(confirmAction.id);
          toast({ title: 'Branch promoted' });
          break;
        case 'rollback':
          await rollbackMutation.mutateAsync(confirmAction.id);
          toast({ title: 'Rolled back' });
          break;
        case 'abandon':
          await abandonMutation.mutateAsync(confirmAction.id);
          toast({ title: 'Branch abandoned' });
          break;
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: `${confirmAction.kind.charAt(0).toUpperCase() + confirmAction.kind.slice(1)} failed`,
        description: extractErrorMessage(err),
      });
    } finally {
      setConfirmAction(null);
    }
  };

  const confirmDialogTitle = (() => {
    if (!confirmAction) return '';
    switch (confirmAction.kind) {
      case 'promote':
        return `Promote branch ${confirmAction.label} to live?`;
      case 'rollback':
        return `Roll back live configuration to commit ${confirmAction.label}?`;
      case 'abandon':
        return `Abandon branch ${confirmAction.label}?`;
    }
  })();

  const confirmDialogDescription = (() => {
    if (!confirmAction) return '';
    switch (confirmAction.kind) {
      case 'promote':
        return 'The branch head will become the live configuration. This cannot be undone automatically.';
      case 'rollback':
        return 'The live configuration will revert to this commit. A new commit will be created on main.';
      case 'abandon':
        return 'The branch will be marked as abandoned and can no longer receive commits.';
    }
  })();

  const isDestructiveAction =
    confirmAction?.kind === 'rollback' || confirmAction?.kind === 'abandon';

  const renderDiff = () => {
    if (!selectedCommitId) return null;

    if (isInitialCommit) {
      if (snapLoading) {
        return (
          <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-20 w-full" />
          </div>
        );
      }

      if (!afterSnap) {
        return (
          <p className="text-muted-foreground text-sm">
            Initial commit; no previous version to compare.
          </p>
        );
      }

      // Show all fields as added (diff against empty)
      const addedTextFields: Array<{ field: string; value: string }> = [];
      const addedOtherFields: Array<{ field: string; value: unknown }> = [];
      const snap = afterSnap as Record<string, unknown>;

      for (const field of TEXT_FIELDS) {
        const val = snap[field];
        if (val != null && val !== '') {
          addedTextFields.push({ field, value: String(val) });
        }
      }
      for (const key of Object.keys(snap)) {
        if (!TEXT_FIELDS.includes(key) && snap[key] != null) {
          addedOtherFields.push({ field: key, value: snap[key] });
        }
      }

      return (
        <div className="mt-3 space-y-4">
          <p className="text-muted-foreground text-xs italic">
            Initial commit &mdash; all fields shown as added.
          </p>
          {addedTextFields.map((tf) => (
            <div key={tf.field}>
              <p className="mb-1 text-sm font-medium">{tf.field}</p>
              <PromptDiff before="" after={tf.value} />
            </div>
          ))}
          {addedOtherFields.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium">Other fields</p>
              <div className="overflow-x-auto rounded border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="px-3 py-1.5 text-left font-medium">Field</th>
                      <th className="px-3 py-1.5 text-left font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addedOtherFields.map((e) => (
                      <tr key={e.field} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-mono">{e.field}</td>
                        <td className="px-3 py-1.5">
                          <code className="rounded bg-emerald-50 px-1 py-0.5 break-words whitespace-pre-wrap text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                            {typeof e.value === 'string'
                              ? e.value
                              : JSON.stringify(e.value, null, 2)}
                          </code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Non-initial commit: diff between parent and current
    if (diffLoading || snapLoading) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-20 w-full" />
        </div>
      );
    }

    if (diffError) {
      return (
        <p className="text-sm text-red-600 dark:text-red-400">
          Failed to load diff: {extractErrorMessage(diffError)}
        </p>
      );
    }

    if (!beforeSnap || !afterSnap) return null;

    const textDiffs: Array<{ field: string; before: string; after: string }> = [];
    const otherDiffs: Array<{ field: string; before: unknown; after: unknown }> = [];

    for (const field of TEXT_FIELDS) {
      const before = (beforeSnap as Record<string, unknown>)[field] as string | null;
      const after = (afterSnap as Record<string, unknown>)[field] as string | null;
      if (before !== after) {
        textDiffs.push({ field, before: before ?? '', after: after ?? '' });
      }
    }

    if (diffOps) {
      for (const op of diffOps) {
        const top = op.path.split('/').filter(Boolean)[0];
        if (top && !TEXT_FIELDS.includes(top) && !otherDiffs.find((d) => d.field === top)) {
          otherDiffs.push({
            field: top,
            before: (beforeSnap as Record<string, unknown>)[top],
            after: (afterSnap as Record<string, unknown>)[top],
          });
        }
      }
    }

    if (textDiffs.length === 0 && otherDiffs.length === 0) {
      return <p className="text-muted-foreground text-sm">No changes in this commit.</p>;
    }

    return (
      <div className="mt-3 space-y-4">
        {textDiffs.map((tf) => (
          <div key={tf.field}>
            <p className="mb-1 text-sm font-medium">{tf.field}</p>
            <PromptDiff before={tf.before} after={tf.after} />
          </div>
        ))}
        {otherDiffs.length > 0 && (
          <div>
            <p className="mb-1 text-sm font-medium">Other field changes</p>
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-3 py-1.5 text-left font-medium">Field</th>
                    <th className="px-3 py-1.5 text-left font-medium">Before</th>
                    <th className="px-3 py-1.5 text-left font-medium">After</th>
                  </tr>
                </thead>
                <tbody>
                  {otherDiffs.map((e) => (
                    <tr key={e.field} className="border-b last:border-0">
                      <td className="px-3 py-1.5 font-mono">{e.field}</td>
                      <td className="px-3 py-1.5">
                        <code className="rounded bg-rose-50 px-1 py-0.5 break-words whitespace-pre-wrap text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                          {typeof e.before === 'string'
                            ? e.before
                            : JSON.stringify(e.before, null, 2)}
                        </code>
                      </td>
                      <td className="px-3 py-1.5">
                        <code className="rounded bg-emerald-50 px-1 py-0.5 break-words whitespace-pre-wrap text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                          {typeof e.after === 'string' ? e.after : JSON.stringify(e.after, null, 2)}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <section className="rounded-lg border dark:border-gray-700">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex w-full items-center gap-2 px-4 py-3 text-left"
        >
          {expanded ? (
            <ChevronDown className="text-muted-foreground h-4 w-4" />
          ) : (
            <ChevronRight className="text-muted-foreground h-4 w-4" />
          )}
          <GitBranch className="h-4 w-4" />
          <span className="text-sm font-medium">Repository</span>
          {mainBranch && (
            <span className="text-muted-foreground text-xs">
              main: {mainBranch.head_commit_id?.slice(0, 8)}
            </span>
          )}
        </button>

        {expanded && (
          <div className="space-y-4 border-t px-4 py-3 dark:border-gray-700">
            {/* Branches card */}
            {branchesLoading && (
              <Card>
                <CardContent className="py-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                </CardContent>
              </Card>
            )}

            {branchesError && (
              <Card>
                <CardContent className="py-4">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Failed to load branches: {extractErrorMessage(branchesError)}
                  </p>
                </CardContent>
              </Card>
            )}

            {!branchesLoading && !branchesError && candidateBranches.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Branches</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {candidateBranches.map((b) => (
                      <div
                        key={b.id}
                        className="flex items-center justify-between border-b pb-2 last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="text-primary text-sm font-medium hover:underline"
                            onClick={() => setSelectedBranch(b.name)}
                          >
                            {b.name}
                          </button>
                          <Badge variant={STATUS_VARIANT[b.status]}>{b.status}</Badge>
                          <span className="text-muted-foreground text-xs">
                            {b.head_commit_id?.slice(0, 8)}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          {(b.status === 'active' || b.status === 'staged') && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isMutating}
                              onClick={() =>
                                setConfirmAction({
                                  kind: 'promote',
                                  id: b.id,
                                  label: b.name,
                                })
                              }
                            >
                              Promote
                            </Button>
                          )}
                          {b.status === 'active' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isMutating}
                              onClick={() =>
                                setConfirmAction({
                                  kind: 'abandon',
                                  id: b.id,
                                  label: b.name,
                                })
                              }
                            >
                              Abandon
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Commits card */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    Commits on <span className="font-mono">{selectedBranch}</span>
                  </CardTitle>
                  {selectedBranch !== 'main' && (
                    <Button size="sm" variant="ghost" onClick={() => setSelectedBranch('main')}>
                      View main
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {commitsLoading && (
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-3/4" />
                  </div>
                )}

                {commitsError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Failed to load commits: {extractErrorMessage(commitsError)}
                  </p>
                )}

                {!commitsLoading && !commitsError && (
                  <div className="space-y-1">
                    {commits?.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 border-b py-1.5 last:border-0"
                      >
                        <GitCommit className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        <button
                          type="button"
                          className={`font-mono text-xs hover:underline ${
                            selectedCommitId === c.id
                              ? 'text-primary font-bold'
                              : 'text-muted-foreground'
                          }`}
                          onClick={() =>
                            setSelectedCommitId(selectedCommitId === c.id ? null : c.id)
                          }
                        >
                          {c.id.slice(0, 8)}
                        </button>
                        <span className="truncate text-sm">{c.message}</span>
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          {c.author_type}
                        </Badge>
                        {selectedBranch === 'main' && c.id === mainBranch?.head_commit_id && (
                          <Badge variant="default" className="shrink-0 text-[10px]">
                            HEAD
                          </Badge>
                        )}
                        <span className="text-muted-foreground shrink-0 text-xs">
                          {new Date(c.created_at).toLocaleDateString()}
                        </span>
                        {selectedBranch === 'main' && c.id !== mainBranch?.head_commit_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-auto shrink-0"
                            disabled={isMutating}
                            onClick={() =>
                              setConfirmAction({
                                kind: 'rollback',
                                id: c.id,
                                label: c.id.slice(0, 8),
                              })
                            }
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />
                            Rollback
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Diff card */}
            {selectedCommitId && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Diff for {selectedCommitId.slice(0, 8)}</CardTitle>
                </CardHeader>
                <CardContent>{renderDiff()}</CardContent>
              </Card>
            )}
          </div>
        )}
      </section>

      {/* Confirmation dialog */}
      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            {isDestructiveAction && (
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-400">
                <AlertTriangle className="h-5 w-5" />
              </div>
            )}
            <AlertDialogTitle>{confirmDialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDialogDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isMutating}
              className={
                isDestructiveAction
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
              onClick={executeConfirmedAction}
            >
              {isMutating
                ? 'Processing...'
                : confirmAction?.kind === 'promote'
                  ? 'Promote'
                  : confirmAction?.kind === 'rollback'
                    ? 'Roll back'
                    : 'Abandon'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
